import ApplicationLayout from '@rio-cloud/rio-uikit/lib/es/ApplicationLayout';
import React, { useState } from 'react';
import { AppRoutes } from './routes/AppRoutes';
import { IntlProvider } from 'react-intl';
import { getMessagesForLocale } from './i18n/i18n';
import NotificationsContainer from '@rio-cloud/rio-uikit/lib/es/NotificationsContainer';
import { VehicleSidebar } from './pages/onboarding/components/sidebar/VehicleSidebar';
import { Header } from './components/header/Header';
import { getUser } from './login/loginSlice';
import { useAppSelector } from './state/hooks';
import { TransactionSidebar } from './pages/transactions/components/sidebar/TransactionSidebar';
import { VehicleAssetTree } from './pages/transactions/components/assetTree/VehicleAssetTree';
import { SupportDialog } from './components/dialog/supportDialog/SupportDialog';
import { UserManualDialog } from './components/dialog/userManualDialog/UserManualDialog';
import { EVENT_USER_LANGUAGE_CHANGED } from '@rio-cloud/rio-user-menu-component';
import {
  DEFAULT_LOCALE,
  useLocale,
  UserMenuLanguageChangedEvent,
} from './i18n/locale';
import {
  graphqlBaseSubscription,
  GraphQlOperation,
  Subscription,
} from './api/graphql/baseQuery';
import { onTransactionUpdated } from './api/graphql/subscriptions';
import {
  SubscriptionEvent,
  TransactionEvent,
} from './api/graphql/realtimeDtos';
import { useTransactionUpdateState } from './state/transactions/transactionUpdateSlice';
import { BookingSidebar } from './pages/bookings/components/sidebar/BookingSidebar';
import { usePaymentMethodUpdateNotification } from './components/usePaymentMethodUpdateNotification';
import { useEffectOnce } from '@rio-cloud/rio-uikit';

export const App: React.FC = () => {
  const { locale, setLocale } = useLocale();
  const [messages, setMessages] = useState<Record<string, string>>();
  const user = useAppSelector(getUser);

  usePaymentMethodUpdateNotification();

  const { setHasTransactionUpdate } = useTransactionUpdateState();

  const loadTranslations = async (language: string) => {
    let data;
    try {
      data = await getMessagesForLocale(language);
      setLocale(language);
    } catch {
      data = await getMessagesForLocale(DEFAULT_LOCALE);
    }
    setMessages(data);
  };

  const handleLanguageChanged = (event: Event) => {
    const language = (event as UserMenuLanguageChangedEvent).detail?.language;
    loadTranslations(language);
  };

  const handleTransactionUpdate = (
    event: SubscriptionEvent<TransactionEvent>,
  ) => {
    setHasTransactionUpdate(true);
  };

  const subscribeToTransactionUpdates = () => {
    const subscribeTransactionsRequest: GraphQlOperation<Subscription> = {
      user,
      request: onTransactionUpdated,
      requestParams: { account_id: user.accountId },
    };
    graphqlBaseSubscription(
      subscribeTransactionsRequest,
      handleTransactionUpdate,
    );
  };

  useEffectOnce(() => {
    subscribeToTransactionUpdates();

    loadTranslations(user.locale);
    document.addEventListener(
      EVENT_USER_LANGUAGE_CHANGED,
      handleLanguageChanged,
    );
  });

  return (
    messages && (
      <IntlProvider
        defaultLocale={DEFAULT_LOCALE}
        locale={locale}
        messages={messages}
      >
        <ApplicationLayout>
          <ApplicationLayout.Header>
            <Header />
          </ApplicationLayout.Header>
          <VehicleSidebar />
          <VehicleAssetTree />
          <TransactionSidebar />
          <BookingSidebar />
          <ApplicationLayout.Body>
            <SupportDialog />
            <UserManualDialog />
            <NotificationsContainer />
            <AppRoutes />
          </ApplicationLayout.Body>
        </ApplicationLayout>
      </IntlProvider>
    )
  );
};
